import { SelectTriggerButton } from '@plato/ui-legacy/components/ui/select-trigger-button';
import { formatShortDate } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { useDateFnsLocale } from '@plato/ui-legacy/lib/hooks/use-date-fns-locale';
import { DateRangePicker, type DateRangePreset } from '@plato/ui/components/date-picker';
import { isSameDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomDateTimeRange } from '../../server/utils/explorer-utils.ts';
import { VisibilityWindow, dateRangeLookup, getDateRange } from '../../server/utils/explorer-utils.ts';

export type PresetDatePickerValue = {
    preset: VisibilityWindow;
    /** Only meaningful for `CUSTOM_DATES` — every other preset computes its own range. */
    dateRange: CustomDateTimeRange | null;
};

export type PresetDatePickerProps = {
    value: PresetDatePickerValue;
    onChange: (value: PresetDatePickerValue) => void;
};

/** Every window that computes its own range — `CUSTOM_DATES` is the calendar itself. */
const computedWindows = [
    VisibilityWindow.TODAY,
    VisibilityWindow.YESTERDAY,
    VisibilityWindow.LAST_WEEK,
    VisibilityWindow.LAST_MONTH,
    VisibilityWindow.LAST_QUARTER,
    VisibilityWindow.TTM,
    VisibilityWindow.FULL_YEAR,
    VisibilityWindow.YTD,
] as const;

/** A committed range read back as a preset, so "the last 7 days" keeps its name. */
function matchPreset(range: CustomDateTimeRange): VisibilityWindow {
    const matched = computedWindows.find((preset) => {
        const { from, to } = dateRangeLookup[preset]();
        return isSameDay(from, range.from) && isSameDay(to, range.to);
    });

    return matched ?? VisibilityWindow.CUSTOM_DATES;
}

/**
 * The timeframe filter: preset windows plus a custom range, committed together.
 * The trigger names the preset rather than spelling out its dates, so only a
 * custom range shows as `from – to`.
 */
export function PresetDatePicker({ value, onChange }: PresetDatePickerProps): ReactElement {
    const { t } = useTranslation('preset-date-picker');
    const locale = useResolvedLocale();
    const dateFnsLocalization = useDateFnsLocale(locale);
    const [open, setOpen] = useState(false);

    const presetLabel = (preset: VisibilityWindow): string => {
        switch (preset) {
            case VisibilityWindow.TODAY:
                return t('option__today');
            case VisibilityWindow.YESTERDAY:
                return t('option__yesterday');
            case VisibilityWindow.LAST_WEEK:
                return t('option__last_week');
            case VisibilityWindow.LAST_MONTH:
                return t('option__last_month');
            case VisibilityWindow.LAST_QUARTER:
                return t('option__last_quarter');
            case VisibilityWindow.TTM:
                return t('option__ttm');
            case VisibilityWindow.FULL_YEAR:
                return t('option__full_year');
            case VisibilityWindow.YTD:
                return t('option__ytd');
            case VisibilityWindow.CUSTOM_DATES:
                return t('option__custom');
        }
    };

    // Yesterday is matchable but never offered, as in the picker this replaces.
    const presets: DateRangePreset[] = computedWindows
        .filter((preset) => preset !== VisibilityWindow.YESTERDAY)
        .map((preset) => ({ label: presetLabel(preset), getValue: dateRangeLookup[preset] }));

    const isCustom = value.preset === VisibilityWindow.CUSTOM_DATES;
    const triggerText = isCustom
        ? value.dateRange &&
          `${formatShortDate(value.dateRange.from, locale)} – ${formatShortDate(value.dateRange.to, locale)}`
        : presetLabel(value.preset);

    return (
        <DateRangePicker
            title={t('picker__title')}
            placeholder={t('option__custom')}
            presets={presets}
            customDatesLabel={t('option__custom')}
            applyLabel={t('apply', { ns: 'common' })}
            cancelLabel={t('form__cancel', { ns: 'common' })}
            locale={dateFnsLocalization}
            open={open}
            onOpenChange={setOpen}
            value={getDateRange(value.preset, value.dateRange) ?? undefined}
            onValueChange={(range) => {
                if (!range?.from || !range.to) {
                    return;
                }

                const committed = { from: range.from, to: range.to };
                const preset = matchPreset(committed);
                onChange({
                    preset,
                    dateRange: preset === VisibilityWindow.CUSTOM_DATES ? committed : null,
                });
            }}
            render={
                <SelectTriggerButton>
                    <div className="flex gap-2 items-center">
                        <CalendarIcon className="h-4 w-4" />
                        <span className="text-sm text-neutral-900">{triggerText ?? t('option__custom')}</span>
                    </div>
                </SelectTriggerButton>
            }
        />
    );
}
